import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { Tabs } from "antd";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useHasAccess } from "~/Hooks/useHasAccess";
import { post, put } from "~/Services";
import { magpHost } from "~/Utils";
import { fields } from "./constants";
import { usePurchaseQR, usePurchaseSuccess, useUserProfileSettings } from "./Profile.hook";

export const Profile = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const { openToast } = useNotification();
  const { hasRole } = useHasAccess();

  const {
    data: userProfileSettingsData,
    error,
    isLoading,
    isError,
    refetch: refetchProfileSettings,
  } = useUserProfileSettings();
  const {
    data: userProfileSettingsDataById,
    isLoading: isLoadingUserProfile,
    refetch: refetchProfileSettingsById,
  } = useDocumentDetails(query?.get("id") || "");
  const { data: purchaseQRData, isLoading: purchaseQRLoading, refetch: refetchPurchaseQR } = usePurchaseQR();
  const {
    data: purchaseQRDataById,
    isLoading: isLoadingpurchaseQR,
    refetch: refetchPurchaseQRById,
  } = useDocumentDetails(query?.get("id") || "");
  const {
    data: purchaseSuccessData,
    isLoading: purchaseSuccessLoading,
    refetch: refetchPurchaseSuccess,
  } = usePurchaseSuccess();
  const {
    data: purchaseSuccessDataById,
    isLoading: isLoadingpurchaseSuccess,
    refetch: refetchPurchaseSuccessById,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    refetchProfileSettings();
  }, []);

  useEffect(() => {
    if (query.get("id") && query.get("activeTab") === "userProfileSettings") {
      refetchProfileSettingsById();
    }
    if (query.get("id") && query.get("activeTab") === "purchaseQR") {
      refetchPurchaseQRById();
    }
    if (query.get("id") && query.get("activeTab") === "purchaseSuccess") {
      refetchPurchaseSuccessById();
    }
  }, [query.get("id"), query.get("activeTab")]);

  useEffect(() => {
    if (query.get("activeTab") === "userProfileSettings") {
      refetchProfileSettings();
    }
    if (query.get("activeTab") === "purchaseQR") {
      refetchPurchaseQR();
    }
    if (query.get("activeTab") === "purchaseSuccess") {
      refetchPurchaseSuccess();
    }
  }, [query.get("activeTab")]);

  useEffect(() => {
    if (!query.get("activeTab")) {
      navigate(`/dedicated/magp/profile?activeTab=userProfileSettings`, {
        replace: true,
      });
    }
  }, []);

  const userProfileFormConfig: FormConfig = {
    initialValues: {
      title: userProfileSettingsDataById?.payload
        ? userProfileSettingsDataById?.payload?.title
        : userProfileSettingsData?.[0]?.title,
      field_language_image: userProfileSettingsDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "Language_image.png",
              status: "done",
              url: userProfileSettingsDataById?.payload?.field_language_image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "Language_image.png",
              status: "done",
              url: userProfileSettingsData?.[0]?.field_language_image,
            },
          ]),
      field_camera_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_camera_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_camera_en),
      field_camera_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_camera_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_camera_ms),
      field_camera_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_camera_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_camera_zh),
      field_change_pwd_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_change_pwd_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_change_pwd_en),
      field_change_pwd_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_change_pwd_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_change_pwd_ms),
      field_change_pwd_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_change_pwd_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_change_pwd_zh),
      field_favourite_text: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_favourite_text)
        : decodeHTML(userProfileSettingsData?.[0]?.field_favourite_text),
      field_favourite_text_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_favourite_text_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_favourite_text_ms),
      field_favourite_text_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_favourite_text_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_favourite_text_zh),
      field_generated_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_generated_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_generated_ms),
      field_generated_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_generated_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_generated_en),
      field_generated_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_generated_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_generated_zh),
      field_location_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_location_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_location_en),
      field_location_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_location_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_location_ms),
      field_location_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_location_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_location_zh),
      field_login_id_text_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_login_id_text_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_login_id_text_en),
      field_login_id_text_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_login_id_text_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_login_id_text_ms),
      field_login_id_text_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_login_id_text_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_login_id_text_zh),
      field_logout_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_logout_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_logout_en),
      field_logout_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_logout_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_logout_ms),
      field_logout_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_logout_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_logout_zh),
      field_notification_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_notification_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_notification_ms),
      field_notifications_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_notifications_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_notification_ms),
      field_notifications_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_notifications_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_notifications_zh),
      field_permission_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_permission_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_permission_en),

      field_permission_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_permission_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_permission_ms),

      field_permission_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_permission_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_permission_zh),
      field_progress_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_progress_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_progress_en),
      field_progress_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_progress_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_progress_ms),
      field_progress_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_progress_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_progress_zh),
      field_purchase_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_purchase_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_purchase_en),
      field_purchase_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_purchase_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_purchase_ms),
      field_purchase_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_purchase_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_purchase_zh),
      field_purchase_login_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_purchase_login_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_purchase_login_en),
      field_purchase_login_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_purchase_login_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_purchase_login_ms),
      field_purchase_login_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_purchase_login_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_purchase_login_zh),
      field_settings_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_settings_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_settings_zh),
      field_settings_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_settings_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_settings_en),
      field_settings_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_settings_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_settings_ms),
      field_updates_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_updates_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_updates_en),
      field_updates_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_updates_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_updates_ms),
      field_updates_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_updates_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_updates_zh),
      field_user_profile_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_user_profile_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_user_profile_en),
      field_user_profile_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_user_profile_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_user_profile_ms),
      field_user_profile_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_user_profile_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_user_profile_zh),
      field_version_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_version_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_version_en),
      field_version_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_version_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_version_ms),
      field_version_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_version_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_version_zh),
      field_profile_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_profile_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_profile_en),
      field_profile_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_profile_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_profile_ms),
      field_profile_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_profile_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_profile_zh),
      field_edit_profile_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_edit_profile_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_edit_profile_en),
      field_edit_profile_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_edit_profile_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_edit_profile_ms),
      field_edit_profile_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_edit_profile_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_edit_profile_zh),
      field_view_id_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_view_id_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_view_id_en),
      field_view_id_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_view_id_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_view_id_ms),
      field_view_id_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_view_id_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_view_id_zh),
      field_save_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_save_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_save_en),
      field_save_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_save_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_save_ms),
      field_save_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_save_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_save_zh),
      field_cancel_en: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_cancel_en)
        : decodeHTML(userProfileSettingsData?.[0]?.field_cancel_en),
      field_cancel_ms: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_cancel_ms)
        : decodeHTML(userProfileSettingsData?.[0]?.field_cancel_ms),
      field_cancel_zh: userProfileSettingsDataById?.payload
        ? decodeHTML(userProfileSettingsDataById?.payload?.field_cancel_zh)
        : decodeHTML(userProfileSettingsData?.[0]?.field_cancel_zh),
      field_pwd_image: userProfileSettingsDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "Password_image.png",
              status: "done",
              url: userProfileSettingsDataById?.payload.field_pwd_image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "Password_image.png",
              status: "done",
              url: userProfileSettingsData?.[0].field_pwd_image,
            },
          ]),
      field_forward_image: userProfileSettingsDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "Forward_image.png",
              status: "done",
              url: userProfileSettingsDataById?.payload.field_forward_image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "Forward_image.png",
              status: "done",
              url: userProfileSettingsData?.[0].field_forward_image,
            },
          ]),

      field_edit_profile_image: userProfileSettingsDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "Edit_Profile_Image.png",
              status: "done",
              url: purchaseSuccessDataById?.payload?.field_edit_profile_image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "Edit_Profile_Image.png",
              status: "done",
              url: userProfileSettingsData?.[0]?.field_edit_profile_image,
            },
          ]),
      field_delete_icon: userProfileSettingsDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "field_delete_icon.png",
              status: "done",
              url: purchaseSuccessDataById?.payload?.field_delete_icon,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "field_delete_icon.png",
              status: "done",
              url: userProfileSettingsData?.[0]?.field_delete_icon,
            },
          ]),
    },
    fields: fields.userProfileSettings,
  };

  const purchaseSuccessFormConfig: FormConfig = {
    initialValues: {
      field_success_image: purchaseSuccessDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "Success_Image.png",
              status: "done",
              url: purchaseSuccessDataById?.payload?.field_success_image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "Success_image.png",
              status: "done",
              url: purchaseSuccessData?.[0]?.field_success_image,
            },
          ]),
      field_payment_approved_en: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_payment_approved_en)
        : decodeHTML(purchaseSuccessData?.[0]?.field_payment_approved_en),
      field_payment_approved_ms: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_payment_approved_ms)
        : decodeHTML(purchaseSuccessData?.[0]?.field_payment_approved_ms),
      field_payment_approved_zh: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_payment_approved_zh)
        : decodeHTML(purchaseSuccessData?.[0]?.field_payment_approved_zh),
      field_login_id_success_en: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_login_id_success_en)
        : decodeHTML(purchaseSuccessData?.[0]?.field_login_id_success_en),
      field_login_id_success_ms: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_login_id_success_ms)
        : decodeHTML(purchaseSuccessData?.[0]?.field_login_id_success_ms),
      field_login_id_success_zh: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_login_id_success_zh)
        : decodeHTML(purchaseSuccessData?.[0]?.field_login_id_success_zh),
      field_id_status_en: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_id_status_en)
        : decodeHTML(purchaseSuccessData?.[0]?.field_id_status_en),
      field_id_status_ms: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_id_status_ms)
        : decodeHTML(purchaseSuccessData?.[0]?.field_id_status_ms),
      field_id_status_zh: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_id_status_zh)
        : decodeHTML(purchaseSuccessData?.[0]?.field_id_status_zh),
      field_user_id_en: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_user_id_en)
        : decodeHTML(purchaseSuccessData?.[0]?.field_user_id_en),
      field_user_id_ms: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_user_id_ms)
        : decodeHTML(purchaseSuccessData?.[0]?.field_user_id_ms),
      field_user_id_zh: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_user_id_zh)
        : decodeHTML(purchaseSuccessData?.[0]?.field_user_id_zh),
      field_pwd_en: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_pwd_en)
        : decodeHTML(purchaseSuccessData?.[0]?.field_pwd_en),
      field_pwd_ms: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_pwd_ms)
        : decodeHTML(purchaseSuccessData?.[0]?.field_pwd_ms),
      field_pwd_zh: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_pwd_zh)
        : decodeHTML(purchaseSuccessData?.[0]?.field_pwd_zh),
      field_support_en: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_support_en)
        : decodeHTML(purchaseSuccessData?.[0]?.field_support_en),
      field_support_ms: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_support_ms)
        : decodeHTML(purchaseSuccessData?.[0]?.field_support_ms),
      field_support_zh: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_support_zh)
        : decodeHTML(purchaseSuccessData?.[0]?.field_support_zh),
      field_support_email: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_support_email)
        : decodeHTML(purchaseSuccessData?.[0]?.field_support_email),
      field_support_email_ms: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_support_email_ms)
        : decodeHTML(purchaseSuccessData?.[0]?.field_support_email_ms),
      field_support_email_zh: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_support_email_zh)
        : decodeHTML(purchaseSuccessData?.[0]?.field_support_email_zh),
      field_bhrs_en: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_bhrs_en)
        : decodeHTML(purchaseSuccessData?.[0]?.field_bhrs_en),
      field_bhrs_ms: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_bhrs_ms)
        : decodeHTML(purchaseSuccessData?.[0]?.field_bhrs_ms),
      field_bhrs_zh: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_bhrs_zh)
        : decodeHTML(purchaseSuccessData?.[0]?.field_bhrs_zh),
      field_ok_en: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_ok_en)
        : decodeHTML(purchaseSuccessData?.[0]?.field_ok_en),

      field_ok_ms: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_ok_ms)
        : decodeHTML(purchaseSuccessData?.[0]?.field_ok_ms),

      field_ok_zh: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_ok_zh)
        : decodeHTML(purchaseSuccessData?.[0]?.field_ok_zh),
      field_approve_en: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_approve_en)
        : decodeHTML(purchaseSuccessData?.[0]?.field_approve_en),
      field_approve_ms: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_approve_ms)
        : decodeHTML(purchaseSuccessData?.[0]?.field_approve_ms),
      field_approve_zh: purchaseSuccessDataById?.payload
        ? decodeHTML(purchaseSuccessDataById?.payload?.field_approve_zh)
        : decodeHTML(purchaseSuccessData?.[0]?.field_approve_zh),
    },
    fields: fields.purchaseSuccess,
  };

  const purchaseQRFormConfig: FormConfig = {
    initialValues: {
      title: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.title)
        : decodeHTML(purchaseQRData?.[0]?.title),
      field_login_id_purchase: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_login_id_purchase)
        : decodeHTML(purchaseQRData?.[0]?.field_login_id_purchase),
      field_login_purchase_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_login_purchase_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_login_purchase_ms),
      field_login_purchase_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_login_purchase_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_login_purchase_zh),
      field_payment_txt_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_payment_txt_en)
        : decodeHTML(purchaseQRData?.[0]?.field_payment_txt_en),
      field_payment_txt_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_payment_txt_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_payment_txt_ms),
      field_payment_txt_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_payment_txt_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_payment_txt_zh),
      field_confirm_txt_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_confirm_txt_en)
        : decodeHTML(purchaseQRData?.[0]?.field_confirm_txt_en),
      field_confirm_txt_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_confirm_txt_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_confirm_txt_ms),
      field_confirm_txt_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_confirm_txt_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_confirm_txt_zh),
      field_account_details_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_account_details_en)
        : decodeHTML(purchaseQRData?.[0]?.field_account_details_en),
      field_account_details_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_account_details_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_account_details_ms),
      field_account_details_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_account_details_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_account_details_zh),
      field_bank_name_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_bank_name_en)
        : decodeHTML(purchaseQRData?.[0]?.field_bank_name_en),
      field_bank_name_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_bank_name_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_bank_name_ms),
      field_bank_name_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_bank_name_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_bank_name_zh),
      field_account_no_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_account_no_en)
        : decodeHTML(purchaseQRData?.[0]?.field_account_no_en),
      field_account_no_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_account_no_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_account_no_ms),
      field_account_no_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_account_no_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_account_no_zh),
      field_name_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_name_en)
        : decodeHTML(purchaseQRData?.[0]?.field_name_en),
      field_name_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_name_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_name_ms),
      field_name_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_name_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_name_zh),
      field_total_pay_amt_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_total_pay_amt_en)
        : decodeHTML(purchaseQRData?.[0]?.field_total_pay_amt_en),
      field_total_pay_amt_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_total_pay_amt_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_total_pay_amt_ms),
      field_total_pay_amt_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_total_pay_amt_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_total_pay_amt_zh),

      field_scan_txt_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_scan_txt_en)
        : decodeHTML(purchaseQRData?.[0]?.field_scan_txt_en),

      field_scan_txt_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_scan_txt_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_scan_txt_ms),
      field_scan_txt_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_scan_txt_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_scan_txt_zh),
      field_upload_txt_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_upload_txt_en)
        : decodeHTML(purchaseQRData?.[0]?.field_upload_txt_en),
      field_upload_txt_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_upload_txt_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_upload_txt_ms),

      field_upload_txt_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_upload_txt_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_upload_txt_zh),

      field_choose_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_choose_en)
        : decodeHTML(purchaseQRData?.[0]?.field_choose_en),
      field_choose_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_choose_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_choose_ms),
      field_choose_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_choose_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_choose_zh),
      field_format_en: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_format_en)
        : decodeHTML(purchaseQRData?.[0]?.field_format_en),
      field_format_ms: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_format_ms)
        : decodeHTML(purchaseQRData?.[0]?.field_format_ms),
      field_format_zh: purchaseQRDataById?.payload
        ? decodeHTML(purchaseQRDataById?.payload?.field_format_zh)
        : decodeHTML(purchaseQRData?.[0]?.field_format_zh),
    },
    fields: fields.purchaseQR,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let excludedKeys: any = [];
    // for (let key in data) {
    //   if (typeof data[key] === "string" && !excludedKeys.includes(key)) {
    //     data[key] = encodeHTML(data[key]);
    //   }
    // }
    console.log("status-1234", status);
    let imageKeys: any = [
      "field_language_image",
      "field_delete_icon",
      "field_pwd_image",
      "field_forward_image",
      "field_edit_profile_image",
      "field_success_image",
    ];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "MAGP");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    const payload = {
      action: action,
      method: method,
      module: "PROFILE",
      subModule: query?.get("activeTab"),
      type: "DEDICATED",
      application: "MAGP",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAGP",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...data,
        field_camera_created_at: "2024-04-09T01:03:09",
        field_camera_updated_at: "2024-04-09T01:03:09",
        published: true,
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response:any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });

          response.status === 'OK' && status=== "PENDING" ? '/dedicated/status-information?activeTab=pending' : '/dedicated/status-information?activeTab=draft'
          refetchPurchaseSuccessById();
          refetchPurchaseQRById();
          refetchProfileSettingsById();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response:any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          response.status === 'OK' && status=== "PENDING" ? '/dedicated/status-information?activeTab=pending' : '/dedicated/status-information?activeTab=draft'
          refetchPurchaseSuccessById();
          refetchPurchaseQRById();
          refetchProfileSettingsById();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
    if (status == "PENDING") {
      navigate("/dedicated/status-information?activeTab=pending");
    } else{
      navigate("/dedicated/status-information?activeTab=draft");
    }
  };

  const items = [
    {
      label: "User Profile Settings",
      key: "userProfileSettings",
      children: (
        <DynamicForm
          isLoading={
            isLoading ||
            purchaseSuccessLoading ||
            isLoadingUserProfile ||
            purchaseQRLoading ||
            isLoadingpurchaseSuccess ||
            isLoadingpurchaseQR
          }
          config={userProfileFormConfig}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/get-user-profile/${userProfileSettingsData?.[0]?.id}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },
    {
      label: "Purchase QR",
      key: "purchaseQR",
      children: (
        <DynamicForm
          isLoading={
            isLoading ||
            purchaseSuccessLoading ||
            isLoadingUserProfile ||
            purchaseQRLoading ||
            isLoadingpurchaseSuccess ||
            isLoadingpurchaseQR
          }
          config={purchaseQRFormConfig}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/get-purhcase-qr/${purchaseQRData?.[0]?.id}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },
    {
      label: "Purchase Success",
      key: "purchaseSuccess",
      children: (
        <DynamicForm
          isLoading={
            isLoading ||
            purchaseSuccessLoading ||
            isLoadingUserProfile ||
            purchaseQRLoading ||
            isLoadingpurchaseSuccess ||
            isLoadingpurchaseQR
          }
          config={purchaseSuccessFormConfig}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${magpHost}/api/get-purchase-success/${purchaseSuccessData?.[0]?.id}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
        />
      ),
    },
  ];

  return (
    <ContentWrapper title={"Profile"}>
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={items}
        onChange={(activeKey) => {
          navigate(`${window.location.pathname}?activeTab=${activeKey}`);
        }}
      ></Tabs>
    </ContentWrapper>
  );
};
